import React, { useState, useEffect, useMemo } from "react";
import {
  Checkbox,
  getTheme,
  Icon,
  MessageBar,
  MessageBarType,
  Spinner,
  Stack,
  Text,
} from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/react";
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
} from "@fluentui/react-components";
import {
  PipelineRun,
  RolloutAndProgressionType,
  Ev2ExperimentStage,
} from "../../Types";
import { useToast } from "../../Contexts/ToastContext";
import { postRolloutDetails } from "../../Api/ado.api";
import { cleanRolloutParams } from "./rolloutDetails.utils";
import { useEv2ExperimentParams } from "../../Hooks/UseCurrentExtension";
import SharedDialog from "./SharedDialog";
import { useFetchPipelineId } from "../../Hooks/AdoHooks/UseFetchPipelineId";
import RollbackDialogManualInstructions from "./RolloutDetailsTable/RollbackDialogManualInstructions";
import {
  MPAC_TYPE,
  PROD_TYPE,
  RC_TYPE,
  ROLLBACK_TYPE,
} from "./RolloutDetailsTable/RolloutDetailsEv2Panel/ev2TreatmentStages.consts";
import { getExpEnvironment } from "../../Shared/shared.utils";
import useAppInsights from "../../Hooks/UseAppInsights";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

interface RollbackDialogProps {
  /**
   * Callback used when user dismisses the dialog
   */
  onDismiss: () => void;
  /**
   * Callback used when rollback is successfully submitted
   */
  onRollbackSubmitted: () => void;
  /**
   * Array of pipeline runs to determine available environments for rollback
   */
  pipelineRuns: PipelineRun[];
  /**
   * Map of pipeline run IDs to their EV2 experiment stages
   */
  ev2StatusMap: Record<number, Ev2ExperimentStage[]>;
  /**
   * The configuration specification filename for the rollback
   */
  configSpecFilename: string;
  /**
   * When true, shows loading state for EV2 stages
   */
  isStagesLoading: boolean;
}

/**
 * A dialog component for initiating rollbacks of experiment deployments
 * Allows users to select multiple environments for rollback based on existing pipeline runs
 */
const RollbackDialog: React.FC<RollbackDialogProps> = ({
  onDismiss,
  onRollbackSubmitted,
  pipelineRuns,
  ev2StatusMap,
  configSpecFilename,
  isStagesLoading,
}) => {
  const { showToast } = useToast();
  const { extensionFolder } = useEv2ExperimentParams();
  const { trackOperationEvent, trackExceptionEvent, createOperationTracker } =
    useAppInsights("RollbackDialog");
  const [selectedEnvironments, setSelectedEnvironments] = useState<
    Record<string, boolean>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { value: pipelineId } = useFetchPipelineId(stageType, setError);
  const selectedCount = useMemo(
    () => Object.values(selectedEnvironments).filter(Boolean).length,
    [selectedEnvironments],
  );

  const ev2Statuses = useMemo(
    () =>
      Object.entries(ev2StatusMap)
        .map(([runId, stages]) => {
          const pipelineRun = pipelineRuns.find(
            ({ id }) => id === Number(runId),
          );
          // Determine environment from pipeline run parameters
          const { RolloutAndProgressionType = "" } =
            pipelineRun?.templateParameters || {};
          const env = getExpEnvironment(RolloutAndProgressionType);
          // Certain stage types don't have env in stageName (rollbacks), so we add manually
          return stages.map((stage) => ({
            ...stage,
            environment: env,
          }));
        })
        .flat(),
    [ev2StatusMap, pipelineRuns],
  );

  const { hasRunningRollouts, latestRollouts } = useMemo(() => {
    const latestStagesMap: Record<string, Ev2ExperimentStage> = {};
    const runningStagesMap: Record<string, boolean> = {
      [RC_TYPE]: false,
      [MPAC_TYPE]: false,
      [PROD_TYPE]: false,
    };

    let hasRunningRollouts = false;
    for (const stage of ev2Statuses) {
      const env = stage.environment;
      if (!env || env === "Unknown") continue;

      if (stage.status === "Running") {
        hasRunningRollouts = true;
        runningStagesMap[env] = true;
      }

      // only care about succeeded stages with experiment links
      if (stage.status !== "Succeeded" || !stage.links.experiment) continue;
      const existing = latestStagesMap[env];
      if (
        !existing ||
        // only want the latest rollouts
        Date.parse(stage.startTime) > Date.parse(existing.startTime)
      ) {
        latestStagesMap[env] = stage;
      }
    }

    const latestStages = Object.entries(latestStagesMap).filter(
      ([_, stage]) => !stage.stageName.includes(ROLLBACK_TYPE),
    );

    if (!latestStages.length) {
      return {
        hasRunningRollouts: hasRunningRollouts,
        latestRollouts: [],
      };
    }
    return {
      hasRunningRollouts: hasRunningRollouts,
      latestRollouts: latestStages.map(([env, stage]) => ({
        url: stage.links.experiment,
        environment: env,
        isRunning: runningStagesMap[env],
      })),
    };
  }, [ev2Statuses]);

  useEffect(() => {
    // Initialize selected environments when dialog opens
    if (latestRollouts.length > 0) {
      const initialSelection: Record<string, boolean> = {};
      latestRollouts.forEach(({ environment, isRunning }) => {
        // Select all by default except running rollouts
        initialSelection[environment] = !isRunning;
      });
      setSelectedEnvironments(initialSelection);
    }

    return () => {
      setSelectedEnvironments({});
      setError(null);
      setIsSubmitting(false);
    };
  }, [latestRollouts]);

  const handleEnvironmentChange = (environment: string, checked: boolean) => {
    setSelectedEnvironments((prev) => ({
      ...prev,
      [environment]: checked,
    }));
    trackOperationEvent({
      operation: "FormChange",
      function: "handleEnvironmentChange",
      properties: {
        field: "environment",
        value: { environment, checked },
        initialValue: { environment, checked: !checked },
      },
    });
  };

  const handleStartRollback = async () => {
    const selectedEnvs = Object.entries(selectedEnvironments)
      .filter(([, selected]) => selected)
      .map(([env]) => env);

    if (selectedEnvs.length === 0) {
      setError("Please select at least one environment to rollback.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!pipelineId) {
        throw new Error("No pipeline configured for this extension.");
      }
      const extensionPath = extensionFolder?.extensionRepoPath;
      if (!extensionFolder) {
        throw new Error(
          "Extension data is missing: Unable to find extension path.",
        );
      }

      // Start rollback for each selected environment
      const rollbackPromises = selectedEnvs.map(async (env) => {
        const rolloutType = ENVIRONMENT_ROLLBACK_MAP[env];
        const rolloutParams = {
          ConfigSpecFileName: configSpecFilename,
          ConfigSpecPath: `Extensions/${extensionPath}`,
          RolloutAndProgressionType: rolloutType,
          ValidateOnly: false,
        };

        const cleanedParams = cleanRolloutParams(rolloutParams);
        const tracker = createOperationTracker(
          "Rollback",
          "handleStartRollback",
        );
        tracker.start({
          environment: env,
          rolloutType: rolloutType,
          params: cleanedParams,
        });
        try {
          const result = await postRolloutDetails(pipelineId, cleanedParams);
          tracker.succeed({ environment: env });
          return result;
        } catch (err: any) {
          tracker.fail(err.message, { environment: env });
          throw err;
        }
      });

      await Promise.all(rollbackPromises);

      showToast({
        message: `Rollback started successfully for ${selectedEnvs.join(
          ", ",
        )}!`,
        type: "success",
        dismissible: true,
        duration: 3000,
      });

      onRollbackSubmitted();
      onDismiss();
    } catch (err: any) {
      trackExceptionEvent(
        err.message || "Failed to start rollback",
        SeverityLevel.Error,
        { function: "handleStartRollback" },
      );
      setError(err.message || "Failed to start rollback");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SharedDialog
      isOpen={true}
      onDismiss={onDismiss}
      onConfirm={handleStartRollback}
      title="Start Rollback"
      subText={
        "Select the environments you want to rollback. This action will initiate rollback pipeline runs for the selected environments."
      }
      isSubmitting={isSubmitting}
      confirmButtonDisabled={latestRollouts.length === 0 || selectedCount === 0}
      error={error}
    >
      {isStagesLoading ? (
        <Spinner label="Loading environments..." />
      ) : latestRollouts.length === 0 ? (
        <MessageBar messageBarType={MessageBarType.info}>
          No environments available for rollback. Rollback is only available for
          environments that have existing pipeline runs.
        </MessageBar>
      ) : (
        <Stack>
          <div className={styles.sectionHeader}>Available Environments:</div>
          <div className={styles.environmentList}>
            {latestRollouts.map(({ environment, isRunning }) => (
              <Stack
                key={environment}
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
              >
                {isRunning && (
                  <Icon
                    iconName="Warning"
                    title="Rollback unavailable while rollout is running"
                    className={styles.checkboxWarningIcon}
                  />
                )}
                <Checkbox
                  key={environment}
                  label={environment}
                  checked={selectedEnvironments[environment] || false}
                  onChange={(_, checked) =>
                    handleEnvironmentChange(environment, checked || false)
                  }
                  className={styles.environmentCheckbox}
                  disabled={isRunning}
                />
                {isRunning && (
                  <Text variant="small" className={styles.checkboxWarningText}>
                    Rollback unavailable while rollout is running
                  </Text>
                )}
              </Stack>
            ))}
          </div>
          {selectedCount > 1 && (
            <MessageBar
              messageBarType={MessageBarType.warning}
              className={styles.warningMessage}
            >
              You have selected {selectedCount} environments. This will create{" "}
              {selectedCount} separate pipeline runs.
            </MessageBar>
          )}
        </Stack>
      )}
      {hasRunningRollouts && (
        <Accordion collapsible>
          <MessageBar messageBarType={MessageBarType.info}>
            Rollback is not available while a rollout is running. Please wait
            for the rollout to complete before starting a rollback or follow the
            instructions below to cancel the running rollout.
          </MessageBar>
          <AccordionItem value="manual-instructions">
            <AccordionHeader>How to cancel a running rollout</AccordionHeader>
            <AccordionPanel>
              <RollbackDialogManualInstructions
                latestRollouts={latestRollouts}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </SharedDialog>
  );
};

// Map environments to their rollback types
const ENVIRONMENT_ROLLBACK_MAP: Record<string, RolloutAndProgressionType> = {
  RC: RolloutAndProgressionType.RC_ROLLBACK,
  MPAC: RolloutAndProgressionType.MPAC_ROLLBACK,
  PROD: RolloutAndProgressionType.PROD_ROLLBACK,
};

const stageType = "single";

const theme = getTheme();
const styles = mergeStyleSets({
  sectionHeader: {
    fontWeight: "600",
    marginBottom: "12px",
    fontSize: "14px",
  },
  environmentList: {
    marginBottom: "16px",
  },
  environmentCheckbox: {
    marginBottom: "8px",
    "& .ms-Checkbox-label": {
      fontSize: "14px",
    },
    marginLeft: "22px",
  },
  warningMessage: {
    marginBottom: "16px",
  },
  checkboxWarningIcon: {
    color: theme.palette.orange,
    marginBottom: "8px",
  },
  checkboxWarningText: {
    color: theme.semanticColors.warningIcon,
    marginBottom: "8px",
  },
});

export default RollbackDialog;
